import React, { useMemo } from 'react'
import clsx from 'clsx'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Shimmer } from '@wartek-id/shimmer'

import { assetUrl } from 'utils/url'
import { useKemudahanSurveyDialog } from 'app/KinerjaShared/hooks/use-kemudahan-survey-dialog'
import { useKemudahanSurveyTrackEvent } from 'app/KinerjaShared/hooks/use-kemudahan-survey-track-event'
import { useAuth } from 'utils/auth'
import { useAPIPelaksanaanProgress } from 'api/kinerja/useAPIKinerja'
import { PRAKTIK_KINERJA_STEP_ORDER } from 'app/KinerjaPamongPage/constants/praktik-kinerja-steps'

type PelaksanaanKemudahanSurveyCardProps = {
  className?: string
}

export function PelaksanaanKemudahanSurveyCard({
  className,
}: PelaksanaanKemudahanSurveyCardProps) {
  // -----------------------------------------
  // Get Pelaksanaan Progress Data
  // -----------------------------------------
  const {
    data: pelaksanaanProgressData,
    isLoading: isPelaksanaanProgressLoading,
  } = useAPIPelaksanaanProgress({
    tipe: 'SPNF',
    opts: {
      enabled: true,
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const isPraktikKinerjaDone = useMemo(() => {
    const currentStep = pelaksanaanProgressData?.data?.state
    const currentStepIndex = PRAKTIK_KINERJA_STEP_ORDER.indexOf(currentStep)

    const refleksiEvaluasiStepIndex = PRAKTIK_KINERJA_STEP_ORDER.indexOf(
      'DOCUMENT_REFLECTION_FOLLOW_UP'
    )

    return currentStepIndex > refleksiEvaluasiStepIndex
  }, [pelaksanaanProgressData])

  // -------------------------
  // Handle Kemudahan Survey
  // -------------------------
  const { session } = useAuth()
  const ptkId = session?.user?.ptkId

  const {
    isEnabled: isSurveyEnabled,
    isSubmitted: isSurveySubmitted,
    markAsSubmitted: markAsSubmittedSurvey,
  } = useKemudahanSurveyDialog({
    type: 'pelaksanaan-pamong',
    additionalKey: ptkId,
  })

  const { trackEvent: trackSurveyEvent } = useKemudahanSurveyTrackEvent()

  const handleClickSurvey = () => {
    trackSurveyEvent({
      actor: 'pamong',
      eventAction: 'view_gform',
      eventCategory: 'survey_kemudahan_pelaksanaan',
    })
    markAsSubmittedSurvey()
  }

  // -------------------------
  // Resolve visibility
  // -------------------------
  const shouldShow =
    isSurveyEnabled && !isSurveySubmitted && isPraktikKinerjaDone

  if (isPelaksanaanProgressLoading) {
    return <Shimmer className={clsx('min-w-80', className)} height={160} />
  }

  if (!shouldShow) {
    return null
  }

  return (
    <section
      className={clsx(
        'p-6 bg-white rounded-lg shadow-card space-y-4',
        className
      )}
    >
      <div className="flex gap-4 items-start">
        <img
          src={assetUrl('/illustrations/img-term-condition-TnC-list.png')}
          className="w-16 h-16 flex-shrink-0"
          alt=""
        />
        <Text variant="body-sm">
          Ada saran dan masukan untuk tahap <strong>Pelaksanaan</strong>?
          Ceritakan ke kami melalui survei.
        </Text>
      </div>

      <Button
        as="a"
        href="https://forms.gle/Y2oPxf39zTU41huz9"
        target="_blank"
        rel="noopener noreferrer"
        size="lg"
        color="white"
        fullWidth
        onClick={handleClickSurvey}
      >
        Isi Survei Pelaksanaan
      </Button>
    </section>
  )
}
